import React from 'react'
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  title: 'Migrating from earlier Core Exchange versions',
  section: 'Migrating from earlier Core Exchange versions',
  layout: 'guide',
  description: 'Upgrade an existing Core Exchange integration to get the latest functionality',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Migrating from earlier Core Exchange versions' subtitle={metadata.description} mdxType="Header" />
      <p>{`This guide covers every breaking change and major new capability added to Core Exchange from v4.6 through v6.4, in version order. Each item is tagged with the version that introduced it. For the full field-by-field history, see the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/changelog"
        }}>{`Changelog`}</a>{`.`}</p>
      <h2 {...{
        "id": "why-migrate"
      }}>{`Why migrate`}</h2>
      <p>{`v6.x is the current Core Exchange specification, aligned with FDX 6.x, and each version since v4.6 has added capabilities that aren't available on earlier ones — see `}<a parentName="p" {...{
          "href": "#new-capabilities-by-version"
        }}>{`New capabilities by version`}</a>{` below.`}</p>
      <h2 {...{
        "id": "new-capabilities-by-version"
      }}>{`New capabilities by version`}</h2>
      <ul>
        <li parentName="ul"><strong parentName="li">{`v5.0`}</strong>{` — An `}<inlineCode parentName="li">{`AUTHORIZED_USER`}</inlineCode>{` account holder relationship type.`}</li>
        <li parentName="ul"><strong parentName="li">{`v5.1`}</strong>{` — Digital asset holdings and securities (`}<inlineCode parentName="li">{`DIGITALASSET`}</inlineCode>{`), and the `}<inlineCode parentName="li">{`US_RTP`}</inlineCode>{` real-time payment network.`}</li>
        <li parentName="ul"><strong parentName="li">{`v5.2`}</strong>{` — `}<inlineCode parentName="li">{`GET /accounts/{accountId}/statements`}</inlineCode>{` and `}<inlineCode parentName="li">{`GET /accounts/{accountId}/statements/{statementId}`}</inlineCode>{`, for retrieving account statements. Business customer support (`}<inlineCode parentName="li">{`BusinessCustomer`}</inlineCode>{`), so `}<inlineCode parentName="li">{`/customers/current`}</inlineCode>{` can represent a business rather than only an individual. Canadian retirement and tax-advantaged investment account types.`}</li>
        <li parentName="ul"><strong parentName="li">{`v5.3`}</strong>{` — A dedicated `}<inlineCode parentName="li">{`CommercialAccountType`}</inlineCode>{` (see `}<a parentName="li" {...{
            "href": "#commercial-account-types-moved-to-their-own-schema-v53-and-later"
          }}>{`Commercial account types moved to their own schema`}</a>{`).`}</li>
        <li parentName="ul"><strong parentName="li">{`v6.0`}</strong>{` — `}<inlineCode parentName="li">{`GET /accounts/{accountId}/asset-transfer-networks`}</inlineCode>{`, which returns the identifiers needed to submit an inter-institution investment account transfer (ACATS, DTC, or ATON). See `}<a parentName="li" {...{
            "href": "#new-endpoint-asset-transfer-networks-v60-and-later"
          }}>{`New endpoint: asset transfer networks`}</a>{`. Also adds `}<inlineCode parentName="li">{`US_FEDNOW`}</inlineCode>{` (the Federal Reserve's FedNow instant payment service) as an accepted payment network type.`}</li>
        <li parentName="ul"><strong parentName="li">{`v6.2`}</strong>{` — Digital wallet accounts (`}<inlineCode parentName="li">{`DIGITAL_WALLET`}</inlineCode>{` category), the `}<inlineCode parentName="li">{`ZWG`}</inlineCode>{` currency code, and a `}<inlineCode parentName="li">{`PREPAID`}</inlineCode>{` investment account type.`}</li>
        <li parentName="ul"><strong parentName="li">{`v6.3`}</strong>{` — `}<inlineCode parentName="li">{`pageKey`}</inlineCode>{`-based pagination on list endpoints, new savings account types, expanded contact fields (authorized signers, phone number type/network/primary), student loan details (`}<inlineCode parentName="li">{`currentSchool`}</inlineCode>{`, `}<inlineCode parentName="li">{`loanProviderName`}</inlineCode>{`), masked card numbers on transactions, and date-range filtering on statements and transactions.`}</li>
        <li parentName="ul"><strong parentName="li">{`v6.4`}</strong>{` — Interest-rate reporting on deposit, investment, loan, and line-of-credit accounts; overdraft fields on deposit accounts; scheduled payments and bills on line-of-credit accounts; loan APR; and a `}<inlineCode parentName="li">{`productId`}</inlineCode>{` on account descriptors.`}</li>
      </ul>
      <h2 {...{
        "id": "breaking-changes"
      }}>{`Breaking changes`}</h2>
      <h3 {...{
        "id": "some-date-time-fields-converted-to-date-only-v50-and-later"
      }}>{`Some date-time fields converted to date only (v5.0 and later)`}</h3>
      <p>{`Certain fields that were inappropriately treated as date-time but actually represent dates were changed to date fields. Impacted fields are: `}<inlineCode parentName="p">{`LoanAccount.originatingDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`nextPaymentDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`lastPaymentDate`}</inlineCode>{`, and `}<inlineCode parentName="p">{`maturityDate`}</inlineCode>{`; `}<inlineCode parentName="p">{`LineOfCreditAccount.nextPaymentDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`lastPaymentDate`}</inlineCode>{`, and `}<inlineCode parentName="p">{`lastStmtDate`}</inlineCode>{`; and `}<inlineCode parentName="p">{`Holding.currentUnitPriceDate`}</inlineCode>{` and `}<inlineCode parentName="p">{`TaxLot.originalPurchaseDate`}</inlineCode>{` `}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Before v5.0",
          "#": true,
          "Before": true,
          "v5.0": true
        }}>{`{
  "maturityDate": "2050-07-15T00:00:00.000Z"
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# v5.0 or later",
          "#": true,
          "v5.0": true,
          "or": true,
          "later": true
        }}>{`{
  "maturityDate": "2050-07-15"
}
`}</code></pre>
      <p><strong parentName="p">{`What to update:`}</strong>{` if you send a full timestamp for any of these fields, truncate it to the date component.`}</p>
      <h3 {...{
        "id": "state-renamed-to-region-on-address-v50-and-later"
      }}><inlineCode parentName="h3">{`state`}</inlineCode>{` renamed to `}<inlineCode parentName="h3">{`region`}</inlineCode>{` on `}<inlineCode parentName="h3">{`Address`}</inlineCode>{` (v5.0 and later)`}</h3>
      <p>{`The `}<inlineCode parentName="p">{`state`}</inlineCode>{` property on the `}<inlineCode parentName="p">{`Address`}</inlineCode>{` schema was renamed to `}<inlineCode parentName="p">{`region`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` any response-mapping code that sets `}<inlineCode parentName="p">{`state`}</inlineCode>{` needs to set `}<inlineCode parentName="p">{`region`}</inlineCode>{` instead.`}</p>
      <h3 {...{
        "id": "country-and-currency-codes-updated-v53-and-later"
      }}>{`Country and currency codes updated (v5.3 and later)`}</h3>
      <p>{`Country codes moved to the ISO 3166-1 alpha-2 standard, and currency codes were updated to the ISO 4217 list current as of January 1, 2023. `}<inlineCode parentName="p">{`AN`}</inlineCode>{` and `}<inlineCode parentName="p">{`CS`}</inlineCode>{` are no longer valid country codes; `}<inlineCode parentName="p">{`HRK`}</inlineCode>{` is no longer a valid currency code.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` For `}<inlineCode parentName="p">{`HRK`}</inlineCode>{` (Croatian kuna), return `}<inlineCode parentName="p">{`EUR`}</inlineCode>{` instead, as Croatia has adopted the euro. For `}<inlineCode parentName="p">{`AN`}</inlineCode>{` (Netherlands Antilles) and `}<inlineCode parentName="p">{`CS`}</inlineCode>{` (Serbia and Montenegro) map each account to the appropriate code: `}<inlineCode parentName="p">{`CW`}</inlineCode>{` (Curaçao), `}<inlineCode parentName="p">{`SX`}</inlineCode>{` (Sint Maarten), or `}<inlineCode parentName="p">{`BQ`}</inlineCode>{` (Bonaire, Sint Eustatius and Saba) for former `}<inlineCode parentName="p">{`AN`}</inlineCode>{` accounts, and `}<inlineCode parentName="p">{`RS`}</inlineCode>{` (Serbia) or `}<inlineCode parentName="p">{`ME`}</inlineCode>{` (Montenegro) for former `}<inlineCode parentName="p">{`CS`}</inlineCode>{` accounts.`}</p>
      <h3 {...{
        "id": "commercial-account-types-moved-to-their-own-schema-v53-and-later"
      }}>{`Commercial account types moved to their own schema (v5.3 and later)`}</h3>
      <p><inlineCode parentName="p">{`COMMERCIALDEPOSIT`}</inlineCode>{`, `}<inlineCode parentName="p">{`COMMERCIALLOAN`}</inlineCode>{`, `}<inlineCode parentName="p">{`COMMERCIALLINEOFCREDIT`}</inlineCode>{`, and `}<inlineCode parentName="p">{`COMMERCIALINVESTMENT`}</inlineCode>{` are no longer valid values of `}<inlineCode parentName="p">{`DepositAccountType`}</inlineCode>{`, `}<inlineCode parentName="p">{`LoanAccountType`}</inlineCode>{`, `}<inlineCode parentName="p">{`LocAccountType`}</inlineCode>{`, and `}<inlineCode parentName="p">{`InvestmentAccountType`}</inlineCode>{`. They're now reported under a dedicated `}<inlineCode parentName="p">{`CommercialAccountType`}</inlineCode>{`, using a new `}<inlineCode parentName="p">{`commercialAccount`}</inlineCode>{` wrapper key in the `}<inlineCode parentName="p">{`/accounts`}</inlineCode>{` list response:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Before v5.3",
          "#": true,
          "Before": true,
          "v5.3": true
        }}>{`{
  "depositAccount": {
    "accountType": "COMMERCIALDEPOSIT"
  }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# v5.3 or later",
          "#": true,
          "v5.3": true,
          "or": true,
          "later": true
        }}>{`{
  "commercialAccount": {
    "accountType": "COMMERCIALDEPOSIT"
  }
}
`}</code></pre>
      <p><strong parentName="p">{`What to update:`}</strong>{` if you report commercial accounts, switch them from their prior account-type wrapper to `}<inlineCode parentName="p">{`commercialAccount`}</inlineCode>{`.`}</p>
      <h3 {...{
        "id": "account-and-transaction-responses-are-no-longer-wrapped-v60-and-later"
      }}>{`Account and transaction responses are no longer wrapped (v6.0 and later)`}</h3>
      <p><inlineCode parentName="p">{`GET /accounts`}</inlineCode>{`, `}<inlineCode parentName="p">{`GET /accounts/{accountId}`}</inlineCode>{`, and `}<inlineCode parentName="p">{`GET /accounts/{accountId}/transactions`}</inlineCode>{` no longer wrap the account or transaction object in a named property that identified its type (for example `}<inlineCode parentName="p">{`depositAccount`}</inlineCode>{` or `}<inlineCode parentName="p">{`depositTransaction`}</inlineCode>{`). Each object is returned directly and must carry a required discriminator field, `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{`, identifying its type:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Before v6.0",
          "#": true,
          "Before": true,
          "v6.0": true
        }}>{`{
  "depositAccount": {
    "accountId": "12345",
    "accountNumberDisplay": "...5678"
  }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# v6.0 or later",
          "#": true,
          "v6.0": true,
          "or": true,
          "later": true
        }}>{`{
  "accountCategory": "DEPOSIT_ACCOUNT",
  "accountId": "12345",
  "accountNumberDisplay": "...5678"
}
`}</code></pre>
      <p>{`The accepted values differ by endpoint:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Endpoint`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Accepted accountCategory values`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GET /accounts`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ANNUITY_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`COMMERCIAL_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`DEPOSIT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`DIGITAL_WALLET`}</inlineCode>{` (v6.2 and later), `}<inlineCode parentName="td">{`INSURANCE_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`INVESTMENT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOAN_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOC_ACCOUNT`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GET /accounts/{accountId}`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DEPOSIT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`INVESTMENT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOAN_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOC_ACCOUNT`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`GET /accounts/{accountId}/transactions`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`DEPOSIT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`INVESTMENT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOAN_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="td">{`LOC_ACCOUNT`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <p>{`The `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` field itself is new in v6.0; the categories it names correspond to the wrapper keys it replaces.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` any response-mapping code keyed on the wrapper property name (`}<inlineCode parentName="p">{`depositAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`investmentAccount`}</inlineCode>{`, etc.) needs to key on `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` instead, and your mapping must explicitly set that field on every account and transaction you return.`}</p>
      <h3 {...{
        "id": "investment-holdings-use-a-securityids-array-v60-and-later"
      }}>{`Investment holdings use a `}<inlineCode parentName="h3">{`securityIds`}</inlineCode>{` array (v6.0 and later)`}</h3>
      <p>{`An investment holding previously identified its security with a single `}<inlineCode parentName="p">{`securityId`}</inlineCode>{`/`}<inlineCode parentName="p">{`securityIdType`}</inlineCode>{` pair. That pair is replaced by a `}<inlineCode parentName="p">{`securityIds`}</inlineCode>{` array of `}<inlineCode parentName="p">{`{ id, idType }`}</inlineCode>{` objects, allowing a holding to report multiple identifier types for the same security.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Before v6.0",
          "#": true,
          "Before": true,
          "v6.0": true
        }}>{`{
  "securityId": "037833100",
  "securityIdType": "CUSIP"
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# v6.0 or later",
          "#": true,
          "v6.0": true,
          "or": true,
          "later": true
        }}>{`{
  "securityIds": [{ "id": "037833100", "idType": "CUSIP" }]
}
`}</code></pre>
      <p><strong parentName="p">{`What to update:`}</strong>{` map your existing single identifier into a one-element `}<inlineCode parentName="p">{`securityIds`}</inlineCode>{` array at minimum; add additional entries if you have more than one identifier type available for a security.`}</p>
      <h3 {...{
        "id": "telephone-number-country-format-has-stricter-validation-v63-and-later"
      }}>{`Telephone number country format has stricter validation (v6.3 and later)`}</h3>
      <p><inlineCode parentName="p">{`telephones[].country`}</inlineCode>{` now must match the pattern of an optional leading `}<inlineCode parentName="p">{`+`}</inlineCode>{` followed by 1-3 digits, no leading zero. (Previously any string up to 3 characters was accepted.) `}<inlineCode parentName="p">{`maxLength`}</inlineCode>{` also increased from 3 to 4 to accommodate the optional `}<inlineCode parentName="p">{`+`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` confirm every `}<inlineCode parentName="p">{`telephones[].country`}</inlineCode>{` value you send is valid. `}</p>
      <h2 {...{
        "id": "non-breaking-changes"
      }}>{`Non-breaking changes`}</h2>
      <h3 {...{
        "id": "statement-status-is-no-longer-a-fixed-set-of-values-v62-and-later"
      }}>{`Statement status is no longer a fixed set of values (v6.2 and later)`}</h3>
      <p>{`The statement `}<inlineCode parentName="p">{`status`}</inlineCode>{` enum, which had constrained the field to `}<inlineCode parentName="p">{`AVAILABLE`}</inlineCode>{`, `}<inlineCode parentName="p">{`PROCESSING`}</inlineCode>{`, and `}<inlineCode parentName="p">{`FAILED`}</inlineCode>{`, was removed entirely. `}<inlineCode parentName="p">{`status`}</inlineCode>{` is now an unconstrained string.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` no action is required. `}</p>
      <h3 {...{
        "id": "line-of-credit-schema-names-changed-v60-and-later"
      }}>{`Line of credit schema names changed (v6.0 and later)`}</h3>
      <p><inlineCode parentName="p">{`LocAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`LocAccountDescriptor`}</inlineCode>{`, `}<inlineCode parentName="p">{`LocAccountType`}</inlineCode>{`, `}<inlineCode parentName="p">{`LocTransaction`}</inlineCode>{`, and `}<inlineCode parentName="p">{`LocTransactionType`}</inlineCode>{` are renamed to `}<inlineCode parentName="p">{`LineOfCreditAccount`}</inlineCode>{`, `}<inlineCode parentName="p">{`LineOfCreditAccountDescriptor`}</inlineCode>{`, `}<inlineCode parentName="p">{`LineOfCreditAccountType`}</inlineCode>{`, `}<inlineCode parentName="p">{`LineOfCreditTransaction`}</inlineCode>{`, and `}<inlineCode parentName="p">{`LineOfCreditTransactionType`}</inlineCode>{` respectively. The `}<inlineCode parentName="p">{`LOC_ACCOUNT`}</inlineCode>{` discriminator value and every field name and JSON response shape are unchanged.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` regenerate client code or server stubs if you generate them directly from Plaid's OpenAPI file.`}</p>
      <h3 {...{
        "id": "new-endpoint-asset-transfer-networks-v60-and-later"
      }}>{`New endpoint: asset transfer networks (v6.0 and later)`}</h3>
      <p><inlineCode parentName="p">{`GET /accounts/{accountId}/asset-transfer-networks`}</inlineCode>{` returns the identifiers needed to submit inter-institution investment account transfers via ACATS, DTC, or ATON.`}</p>
      <p>{`This endpoint backs `}<a parentName="p" {...{
          "href": "/docs/investments-move"
        }}>{`Investments Move`}</a>{`. Without it, the requesting party falls back to asking the end user to manually enter account and holdings details, which increases the rate of rejected (NIGO) transfer requests reaching your transfer desk. Implementing the endpoint reduces that rejection rate by supplying verified data up front.`}</p>
      <h3 {...{
        "id": "telephone-number-purpose-schema-renamed-cell-deprecated-v63-and-later"
      }}>{`Telephone number purpose schema renamed, `}<inlineCode parentName="h3">{`CELL`}</inlineCode>{` deprecated (v6.3 and later)`}</h3>
      <p>{`The `}<inlineCode parentName="p">{`type`}</inlineCode>{` field on a telephone number now references `}<inlineCode parentName="p">{`TelephoneNumberPurpose`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`TelephoneNumberType`}</inlineCode>{`, and adds `}<inlineCode parentName="p">{`BOTH`}</inlineCode>{` and `}<inlineCode parentName="p">{`PERSONAL`}</inlineCode>{` values. Every previously valid `}<inlineCode parentName="p">{`type`}</inlineCode>{` value, including `}<inlineCode parentName="p">{`CELL`}</inlineCode>{`, is still valid. `}<inlineCode parentName="p">{`CELL`}</inlineCode>{` is marked deprecated in favor of reporting a separate `}<inlineCode parentName="p">{`network`}</inlineCode>{` field as `}<inlineCode parentName="p">{`CELLULAR`}</inlineCode>{`, but existing implementations that keep sending `}<inlineCode parentName="p">{`CELL`}</inlineCode>{` are not broken by this change.`}</p>
      <p><strong parentName="p">{`What to update:`}</strong>{` no action is required. Keep sending `}<inlineCode parentName="p">{`CELL`}</inlineCode>{` for mobile numbers: Plaid doesn't read `}<inlineCode parentName="p">{`network`}</inlineCode>{`, and it reports `}<inlineCode parentName="p">{`PERSONAL`}</inlineCode>{` numbers as home numbers.`}</p>
      <h2 {...{
        "id": "after-migrating"
      }}>{`After migrating`}</h2>
      <ul>
        <li parentName="ul">{`Re-run the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/testing"
          }}>{`Validator`}</a>{` against your updated build before requesting production access for the new version.`}</li>
        <li parentName="ul">{`Update any internal documentation or generated client code that references the old wrapped response shape, `}<inlineCode parentName="li">{`Loc*`}</inlineCode>{` schema names, or `}<inlineCode parentName="li">{`state`}</inlineCode>{` on `}<inlineCode parentName="li">{`Address`}</inlineCode>{`.`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "why-migrate",
        level: 2,
        title: "Why migrate",
      },
{
        id: "new-capabilities-by-version",
        level: 2,
        title: "New capabilities by version",
      },
{
        id: "breaking-changes",
        level: 2,
        title: "Breaking changes",
      },
{
        id: "some-date-time-fields-converted-to-date-only-v50-and-later",
        level: 3,
        title: "Some date-time fields converted to date only (v5.0 and later)",
      },
{
        id: "state-renamed-to-region-on-address-v50-and-later",
        level: 3,
        title: "state renamed to region on Address (v5.0 and later)",
      },
{
        id: "country-and-currency-codes-updated-v53-and-later",
        level: 3,
        title: "Country and currency codes updated (v5.3 and later)",
      },
{
        id: "commercial-account-types-moved-to-their-own-schema-v53-and-later",
        level: 3,
        title: "Commercial account types moved to their own schema (v5.3 and later)",
      },
{
        id: "account-and-transaction-responses-are-no-longer-wrapped-v60-and-later",
        level: 3,
        title: "Account and transaction responses are no longer wrapped (v6.0 and later)",
      },
{
        id: "investment-holdings-use-a-securityids-array-v60-and-later",
        level: 3,
        title: "Investment holdings use a securityIds array (v6.0 and later)",
      },
{
        id: "telephone-number-country-format-has-stricter-validation-v63-and-later",
        level: 3,
        title: "Telephone number country format has stricter validation (v6.3 and later)",
      },
{
        id: "non-breaking-changes",
        level: 2,
        title: "Non-breaking changes",
      },
{
        id: "statement-status-is-no-longer-a-fixed-set-of-values-v62-and-later",
        level: 3,
        title: "Statement status is no longer a fixed set of values (v6.2 and later)",
      },
{
        id: "line-of-credit-schema-names-changed-v60-and-later",
        level: 3,
        title: "Line of credit schema names changed (v6.0 and later)",
      },
{
        id: "new-endpoint-asset-transfer-networks-v60-and-later",
        level: 3,
        title: "New endpoint: asset transfer networks (v6.0 and later)",
      },
{
        id: "telephone-number-purpose-schema-renamed-cell-deprecated-v63-and-later",
        level: 3,
        title: "Telephone number purpose schema renamed, CELL deprecated (v6.3 and later)",
      },
{
        id: "after-migrating",
        level: 2,
        title: "After migrating",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
